import React, { useState } from 'react'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import { useTranslation } from 'react-i18next'

import { BOX_SHADOW_XS, GRAY_400, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { formatPrice } from 'utils/price/priceFormatter'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'

import { usePfStore } from '../../store'
import { CartSidebar } from './CartSidebar.component'
import { StepNavSection } from './StepNavSection.component'
import { useCartDisplay } from './useCartDisplay'
import { PF_MOBILE_BOTTOM_BAR_HEIGHT } from '../stickyOffsets'
import { useOrderExtras } from '../../store/stagingRenovation'
import { useBottomActionBarRef } from 'utils/hooks/bottomActionBarRegistry'

/**
 * MobileBottomBar — fixed bottom action bar for mobile / narrow viewports.
 *
 * Replaces the old floating "View cart" pill (`MobileCartButton`) and is now the
 * mobile NAV hub:
 *   - Left: an interactive `Total` (cart icon + running total + up-chevron) that
 *     opens the cart as a bottom sheet for review/edit. This is how cart access
 *     stays reachable without stealing the top zone (owned by the stepper).
 *   - Right: the per-step nav in `bar` layout — an icon-only Back plus a compact
 *     "To {destination}" continue (the stepper supplies the wayfinding, so the
 *     button stays short). Disabled reason renders inline beneath it.
 *
 * The sheet itself is the existing `CartSidebar` rendered inside a bottom
 * `ResponsiveDrawer` (`title="Summary"`); on mobile the sidebar shows the
 * property indicator + items + totals only (the footer owns nav, so CartSidebar
 * suppresses its own nav row on mobile).
 *
 * Total is read from the same source the sidebar uses (`useCartDisplay`) so the
 * two never disagree. `StepNavSection` owns all routing/gating via `useStepNav`,
 * including the Summary place-order legacy-context isolation.
 */
export const MobileBottomBar: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const [isOpen, setIsOpen] = useState(false)
  const bottomActionBarRef = useBottomActionBarRef<HTMLDivElement>()

  const items = usePfStore((s) => s.items)
  const currentStep = usePfStore((s) => s.currentStep)
  const catalogueQuery = usePfCatalogue()
  const extras = useOrderExtras(catalogueQuery.data)

  const { total, currency } = useCartDisplay(items, catalogueQuery.data, extras)

  const isEmpty = items.length === 0
  const totalLabel = formatPrice(total, currency)

  return (
    <>

      <Box
        ref={bottomActionBarRef}
        data-cy="pf-revamp-mobile-bottom-bar"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: WHITE,
          borderTop: `1px solid ${GRAY_400}`,
          boxShadow: BOX_SHADOW_XS,
          minHeight: PF_MOBILE_BOTTOM_BAR_HEIGHT,
          px: 1.6,
          pt: 1.2,
          pb: 'calc(1.2rem + env(safe-area-inset-bottom))',
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.2}>

          {/* Left — interactive cart access via the running total. The up-chevron
              is the affordance (taps open the cart sheet); no cart icon/badge,
              which read as redundant next to the "Total" label. */}
          <ButtonBase
            onClick={() => setIsOpen(true)}
            aria-label={t('purchase_flow_revamp:cart.see_summary')}
            data-cy="pf-revamp-mobile-cart-toggle"
            sx={{ flexShrink: 0, borderRadius: '0.8rem', px: 0.4, py: 0.4 }}
          >
            <Stack direction="row" alignItems="center" gap={0.4}>

              {isEmpty &&
                <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
                  {t('purchase_flow_revamp:cart.see_summary')}
                </Typography>
              }

              {!isEmpty &&
                <Stack gap={0} alignItems="flex-start">

                  <Typography variant="text-xs" color={GRAY_700} sx={{ lineHeight: 1.2 }}>
                    {t('purchase_flow_revamp:cart.total_label')}
                  </Typography>

                  <Typography variant="text-md" fontWeight={600} color={GRAY_900} sx={{ whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                    {totalLabel}
                  </Typography>

                </Stack>
              }

              <KeyboardArrowUpRoundedIcon sx={{ fontSize: '1.8rem', color: GRAY_700 }} />

            </Stack>
          </ButtonBase>

          {/* Right — per-step nav (icon Back + compact continue) */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StepNavSection step={currentStep} layout="bar" />
          </Box>

        </Stack>
      </Box>

      <ResponsiveDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('purchase_flow_revamp:cart.summary_title')}
        anchor="bottom"
        height="80vh"
        backdrop="dark"
      >

        <CartSidebar />

      </ResponsiveDrawer>

    </>
  )
}
